import {parseCookies} from 'nookies'
import React, {useEffect, useState} from 'react'
import {useRouter} from 'next/router'
import {useRecoilValue} from 'recoil'
import {useMutation, useReactiveVar} from '@apollo/client'
import CartCommentsToOrder from 'modules/cart/components/CartCommentsToOrder'
import CartSummary from 'modules/cart/components/CartSummary'
import CartCrosssell from 'modules/cart/components/CartCrosssell'
import OrderOptions from 'modules/product/components/OrderOptions'
import {addApolloState} from 'helpers/ApolloHelper/ApolloHelper'
import {useGetCartQuery} from '_app/graphql/queries/cart/GetCartDocumentSmall'
import {useUpdateCartItemsMutation} from '_app/graphql/mutations/cart/UpdateCartItemsDocument'
import withAppInit from '_app/hoc/withAppInit'
import {Context} from '_app/types/context'
import {CartFooter, CartItems, CartSidebar, CartWrapper} from 'modules/cart/components'
import {GTM_EVENTS, parseProductsToGTM} from '_app/utils/dataLayer'
import useAttributesMetadata from '_app/hooks/useAttributesMetadata'
import getFreeDeliveryThreshold from '_app/utils/localized/getFreeDeliveryThreshold'
import {gtmItemListNamesAtom} from '_app/state/atoms'
import {CartItem} from '../modules/cart/types'
import {BF_GIFTS_SKUS, validateGiftItems} from '../_app/temp/bf-sale'
import {REMOVE_ITEM_FROM_CART} from '../_app/graphql/mutations/cart/RemoveItemFromCart'
import {cart, cartIdVar} from '../_app/cache'
import {Box, CircularProgress} from '@chakra-ui/react'
import RequestHelper from '../helpers/RequestHelper/RequestHelper'
import I18nHelper from '../helpers/I18nHelper/I18nHelper'

const Cart = () => {
    const router = useRouter()
    const {locale} = router
    const {metaData} = useAttributesMetadata({})
    const gtmItemListNames = useRecoilValue(gtmItemListNamesAtom)
    const [loader, setLoader] = useState(true)
    const cartId: any = useReactiveVar(cartIdVar)
    const style = {
        box: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
        },
        svgLoading: {
            height: '20px',
            position: 'relative',
        }
    }
    const [removeItemFromCart] = useMutation(REMOVE_ITEM_FROM_CART)
    const [updateCartItemsMutation] = useUpdateCartItemsMutation()
    const {data: freshData, loading}: any = useGetCartQuery({
        variables: {
            cartId: cartId,
        },
        skip: !cartId,
    })
    const [data, setData]: any = useState(null)
    useEffect(() => {
        if (freshData) {
            setData(freshData)
        }
    }, [freshData])
    useEffect(() => {
        const productsPriceValue =
            data?.cart?.prices?.subtotal_including_tax?.value ?? 0.0
        if (productsPriceValue === 0.0) {
            return
        }
        const items: CartItem[] = (data?.cart?.items as CartItem[]) ?? []
        const giftItems = items.filter(
            (item: CartItem) =>
                item?.product?.sku && BF_GIFTS_SKUS.includes(item?.product?.sku)
        )
        if (!giftItems.length) return
        const {toDelete, toReduceQt} = validateGiftItems(
            giftItems,
            productsPriceValue
        )
        if (toDelete?.length) {
            toDelete.forEach((toDel: CartItem) => {
                removeItemFromCart({
                    variables: {
                        cartId: parseCookies()?.cartId,
                        cartItemId: toDel?.id,
                    },
                    onCompleted: (res: any) => {
                        RequestHelper.onComplete(res, 'removeItemFromCart', locale)
                        cart(res?.data?.removeItemFromCart)
                        setData(res?.data?.removeItemFromCart)
                    }
                }).catch((error) => RequestHelper.internalServerError(error))
            })
        }
        if (toReduceQt?.length) {
            const toReduce = toReduceQt?.map((toRed: CartItem) => ({
                cart_item_uid: toRed.uid,
                quantity: 1,
            }))
            updateCartItemsMutation({
                variables: {
                    cart_id: parseCookies()?.cartId,
                    cart_items: toReduce,
                },
                onCompleted: (res: any) => {
                    RequestHelper.onComplete(res, 'updateCartItems', locale)
                    cart(res?.data?.updateCartItems)
                    setData(res?.data?.updateCartItems)
                }
            }).catch((error) => RequestHelper.internalServerError(error))
        }
    }, [data])
    useEffect(() => {
        if (!Object.keys(metaData)?.length || loading) return
        if (data?.cart?.items?.length > 0 && metaData && gtmItemListNames && cartId) {
            const payload = {
                ecommerce: {
                    currencyCode: '',
                    checkout: {
                        actionField: {step: 1 as const},
                        cartId,
                        products: parseProductsToGTM(data, metaData, gtmItemListNames),
                    },
                },
            }
            GTM_EVENTS.cart(payload)
        }
    }, [data, metaData, cartId, gtmItemListNames, loading])
    useEffect(() => {
        if (!cartId) {
            setLoader(false)
        }
    }, [cartId])
    useEffect(() => {
        if (cartId) {
            setLoader(true)
            if (data?.cart?.items) {
                setLoader(false)
            }
        } else {
            setLoader(false)
        }
    }, [data, cartId])
    return (
        <CartWrapper>
            {
                loader ? (
                    <Box sx={style.box}>
                        <CircularProgress isIndeterminate sx={{'& svg': style.svgLoading}}/>
                    </Box>
                ) : (
                    <CartItems setData={setData} data={data}/>
                )
            }
            {
                data?.cart?.items?.length !== undefined && data?.cart?.items?.length !== 0 && !loader && (
                    <>
                        <CartCrosssell setData={setData} display={{base: 'block', lg: 'none'}}/>
                        <CartCommentsToOrder/>
                        <CartFooter
                            isDisabled={data?.cart?.prices?.subtotal_including_tax?.value === 0.0}
                            display={{base: 'none', lg: 'flex'}}
                        />
                        <CartSidebar>
                            <CartSummary
                                data={data}
                                borderBottomWidth={{base: 0, lg: '1px'}}
                                productsPriceValue={
                                    data?.cart?.prices?.subtotal_including_tax?.value || 0
                                }
                                setData={setData}
                                productsDiscountPriceValue={
                                    !!data?.cart?.applied_coupons?.[0]?.code
                                        ? data?.cart?.prices?.subtotal_with_discount_excluding_tax
                                            ?.value
                                        : null
                                }
                                productsPriceCurrency={
                                    data?.cart?.prices?.subtotal_including_tax?.currency
                                }
                                {...getFreeDeliveryThreshold(locale, router)}
                            />

                            <CartFooter
                                isDisabled={
                                    data?.cart?.prices?.subtotal_including_tax?.value === 0.0
                                }
                                display={{base: 'flex', lg: 'none'}}
                            />
                             <CartCrosssell setData={setData} display={{base: 'none', lg: 'block'}} my={6}/>
                            <OrderOptions/>
                        </CartSidebar>
                    </>
                )
            }
        </CartWrapper>
    )
}
export const getServerSideProps = withAppInit(async (ctx: Context) => {
    const headingCopy = 'Twoje zakupy z Lilou'
    const translations: any = await I18nHelper.getI18nProps(ctx, ['cart', 'checkout', 'engraving'])
    try {
        return addApolloState(ctx.apolloClient, {
            props: {
                headingCopy,
                ...translations,
            },
        })
    } catch (error) {
        return {
            notFound: true,
        }
    }
})

export default Cart
